import { useEffect, useMemo, useState } from "react";

import { MdOutlinePayments } from "react-icons/md";
import { BsLink45Deg } from "react-icons/bs";
import { BiDetail } from "react-icons/bi";
import { FaUser, FaProjectDiagram, FaHistory } from "react-icons/fa";
import { GrDocumentTime, GrMoney } from "react-icons/gr";
import { ImInsertTemplate } from "react-icons/im";
import { FiFilePlus } from "react-icons/fi";
import { HiOutlineWallet } from "react-icons/hi2";
import { CgSpinner } from "react-icons/cg";
import { IconContext } from "react-icons";
import { NavLink } from "react-router-dom";
import { FaChartLine } from "react-icons/fa";
import { LuChartNoAxesCombined } from "react-icons/lu";
import { BsIncognito } from "react-icons/bs";

import Tooltip from "../../../components/tooltip/Tooltip";

import "../../sidebar/Sidebar.css";

import { getSessionItem } from "../../../utils/helper/session";

/**
 * @function ManagerSidebar
 * @returns {JSX.Element}
 */
function ManagerSidebar() {
  const user = getSessionItem("User");

  const isManager = user?.roleName === "Manager";
  const isCustomer = user?.roleName === "Customer";

  const [projectsReady, setProjectsReady] = useState(false);

  const value = useMemo(
    () => ({
      color: "gray",
      size: "40px",
    }),
    []
  );

  useEffect(() => {
    const checkProjects = () => {
      const projects = getSessionItem("projects");

      const ready =
        Array.isArray(projects) &&
        projects.length > 0;

      setProjectsReady(ready);
    };

    checkProjects();

    const interval = setInterval(checkProjects, 300);

    window.addEventListener("storage", checkProjects);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkProjects);
    };
  }, []);

  /**
   * Renders a spinner while projects are not available.
   */
  const renderManagerIcon = (Icon) => {
    if (!projectsReady) {
      return (
        <CgSpinner
          className="sidebar-loading-icon"
          size="30px"
        />
      );
    }

    return <Icon />;
  };

  /**
   * Applies active/loading classes to manager links.
   */
  const getManagerLinkClassName = ({ isActive }) => {
    const classes = [];

    if (isActive) {
      classes.push("active");
    }

    if (!projectsReady) {
      classes.push("sidebar-link-loading");
    }

    return classes.join(" ");
  };

  /**
   * Prevent navigation while projects are still loading.
   */
  const preventNavigationWhileLoading = (event) => {
    if (!projectsReady) {
      event.preventDefault();
    }
  };

  return (
    <nav
      className="sidebar"
      id="manager-sidebar"
    >
      <IconContext.Provider value={value}>
        <div className="team-nav-group nav-group">
          {isManager && (
            <div>
              <Tooltip
                content={
                  projectsReady
                    ? "Manager Dashboard"
                    : "Loading projects..."
                }
                direction="right"
              >
                <NavLink
                  to="/manager"
                  end
                  onClick={preventNavigationWhileLoading}
                  className={getManagerLinkClassName}
                >
                  {renderManagerIcon(ImInsertTemplate)}
                </NavLink>
              </Tooltip>

              <Tooltip
                content={
                  projectsReady
                    ? "Payroll Details"
                    : "Loading projects..."
                }
                direction="right"
              >
                <NavLink
                  to="/manager/payroll-details"
                  onClick={preventNavigationWhileLoading}
                  className={getManagerLinkClassName}
                >
                  {renderManagerIcon(GrMoney)}
                </NavLink>
              </Tooltip>

              <Tooltip
                content={
                  projectsReady
                    ? "Project Details"
                    : "Loading projects..."
                }
                direction="right"
              >
                <NavLink
                  to="/manager/projects"
                  onClick={preventNavigationWhileLoading}
                  className={getManagerLinkClassName}
                >
                  {renderManagerIcon(FaProjectDiagram)}
                </NavLink>
              </Tooltip>

              <Tooltip
                content={
                  projectsReady
                    ? "Additional Payments"
                    : "Loading projects..."
                }
                direction="right"
              >
                <NavLink
                  to="/manager/additional-payments"
                  onClick={preventNavigationWhileLoading}
                  className={getManagerLinkClassName}
                >
                  {renderManagerIcon(MdOutlinePayments)}
                </NavLink>
              </Tooltip>

              <Tooltip
                content={
                  projectsReady
                    ? "Manual Time Approval"
                    : "Loading projects..."
                }
                direction="right"
              >
                <NavLink
                  to="/manager/manual-time-approval"
                  onClick={preventNavigationWhileLoading}
                  className={getManagerLinkClassName}
                >
                  {renderManagerIcon(FaHistory)}
                </NavLink>
              </Tooltip>

              <Tooltip
                content={
                  projectsReady
                    ? "Job Request"
                    : "Loading projects..."
                }
                direction="right"
              >
                <NavLink
                  to="/manager/job-request"
                  onClick={preventNavigationWhileLoading}
                  className={getManagerLinkClassName}
                >
                  {renderManagerIcon(FiFilePlus)}
                </NavLink>
              </Tooltip>

              <Tooltip
              content="Growth Management"
              direction="right"
            >
              <NavLink
                to="/manager/growth-management"
                className={
                  location.pathname === "/growth-management"
                    ? "active"
                    : ""
                }
              >
                <LuChartNoAxesCombined />
              </NavLink>
            </Tooltip>

            <Tooltip
              content="Anonymous Feedbacks"
              direction="right"
            >
              <NavLink
                to="/manager/anonymous-feedback"
                className={
                  location.pathname === "/anonymous-feedback"
                    ? "active"
                    : ""
                }
              >
                <BsIncognito />
              </NavLink>
            </Tooltip>
            </div>
          )}
        </div>

        <div
          className="team-nav-group nav-group"
          id="step-sidebar"
        >
          {!isCustomer && (
            <div>
              <Tooltip
                content="My Dashboard"
                direction="right"
              >
                <NavLink
                  to="/summary"
                  className={({ isActive }) =>
                    isActive ? "active" : ""
                  }
                >
                  <ImInsertTemplate />
                </NavLink>
              </Tooltip>

              <Tooltip
                content="My Daily Diary"
                direction="right"
              >
                <NavLink
                  to="/diary"
                  className={({ isActive }) =>
                    isActive ? "active" : ""
                  }
                >
                  <BiDetail />
                </NavLink>
              </Tooltip>

              <div id="step-3">
                <Tooltip
                  content="My Info"
                  direction="right"
                >
                  <NavLink
                    to="/profile/info"
                    className={({ isActive }) =>
                      isActive ? "active" : ""
                    }
                  >
                    <FaUser />
                  </NavLink>
                </Tooltip>
              </div>

              <div id="step-3">
                <Tooltip
                  content="My payments"
                  direction="right"
                >
                  <NavLink
                    to="/profile/payment"
                    className={({ isActive }) =>
                      isActive ? "active" : ""
                    }
                  >
                    <HiOutlineWallet />
                  </NavLink>
                </Tooltip>
              </div>

              {!user?.disabled && (
                <Tooltip
                  content="Links"
                  direction="right"
                >
                  <NavLink
                    to="/profile/links"
                    className={({ isActive }) =>
                      isActive ? "active" : ""
                    }
                  >
                    <BsLink45Deg />
                  </NavLink>
                </Tooltip>
              )}

              <Tooltip
                content="My Requests"
                direction="right"
              >
                <NavLink
                  to="/profile/requests"
                  className={({ isActive }) =>
                    isActive ? "active" : ""
                  }
                >
                  <GrDocumentTime />
                </NavLink>
              </Tooltip>

              <Tooltip
                content="My Growth"
                direction="right"
              >
                <NavLink
                  to="/profile/growth"
                  className={({ isActive }) =>
                    isActive ? "active" : ""
                  }
                >
                  <FaChartLine />
                </NavLink>
              </Tooltip>
            </div>
          )}
        </div>

        <div className="team-nav-group nav-group">
          {isCustomer && (
            <Tooltip
              content="Team Dashboard"
              direction="right"
            >
              <NavLink
                to="/team"
                className={({ isActive }) =>
                  isActive ? "active" : ""
                }
              >
                <ImInsertTemplate />
              </NavLink>
            </Tooltip>
          )}
        </div>
      </IconContext.Provider>
    </nav>
  );
}

export default ManagerSidebar;